import { ChartConfigurationOption } from '../../wtFramework';
import { IValueViewModel, SeriesDefinition, TagDateValue, IdentifierType, IStatistics } from '../../models';
import { IFormatNumberHelper } from '../../services/helpers';
import { SeriesService } from '../../services/widgets';
import { IStatisticsService } from '../../services/data';
import { infoTextColor } from '../../common/constants';

export interface IIdentifierDetailsService {
    getChartConfiguration(): ChartConfigurationOption;
    getSeries(vm: IValueViewModel): SeriesDefinition<TagDateValue>[];
    prepareData(data: { values: TagDateValue[], missing?: any }, beginDate: string, endDate: string): { stats: IStatistics, values: TagDateValue[] };
}

export class IdentifierDetailsService implements IIdentifierDetailsService {
    static serviceId = 'identifierDetailsService';

    static $inject = [
        '$translate',
        'formatNumberHelper',
        'seriesService',
        'statisticsService'
    ];

    constructor(
        private _$translate: angular.translate.ITranslateService,
        private _formatNumberHelper: IFormatNumberHelper,
        private _seriesService: SeriesService,
        private _statisticsService: IStatisticsService
    ) {
    }

    getChartConfiguration(): ChartConfigurationOption {
        const options = new ChartConfigurationOption();

        options.isTitleVisible = false;
        options.isLegendVisible = false;
        options.isHorizontalGridlinesVisible = true;
        options.panes = [
            {
                height: 1,
                isXaxisVisible: true,
                isYaxisVisible: true,
                isHorizontalGridLinesVisible: true
            }
        ];
        return options;
    }

    getSeries(vm: IValueViewModel): SeriesDefinition<TagDateValue>[] {
        let result: SeriesDefinition<TagDateValue>[] = [];
        const formatter = this._formatNumberHelper.getFormatter(2);
        switch (vm.identifierType) {
            case IdentifierType.Target:
                result = this.getTargetSeries(vm, formatter);
                break;
            case IdentifierType.CalculatedValue:
                result.push({
                    name: vm.name,
                    identifier: vm.guid + 'average',
                    value: d => d.value,
                    selector: d => d.guid + d.valueType,
                    color: infoTextColor,
                    symbol: 'circle',
                    isLineVisible: false,
                    isSymbolVisible: true,
                    serieState: 0,
                    crosshairFormat: d => formatter.format(d.value),
                    isCrosshairHidden: false
                });
                break;
            case IdentifierType.Validated:
                console.log('Not implemented : getSeries on Validated');
                throw 'Not implemented';
        }
        result.push(this._seriesService.createMissingSerie());
        return result;
    }

    private getTargetSeries(vm: IValueViewModel, formatter: Intl.NumberFormat): SeriesDefinition<TagDateValue>[] {
        const result = [];
        result.push({
            name: vm.name,
            identifier: 'joined',
            selector: d => d.guid,
            color: '#fc3',
            symbol: 'circle',
            isLineVisible: false,
            isSymbolVisible: false,
            crosshairFormat: d => `Source: ${formatter.format(d.value)} - Target: ${formatter.format(d.secondaryValue)}`,
            y0: d => d.value,
            y1: d => d.secondaryValue,
            seriesType: 'area',
            isCrosshairHidden: true
        });
        result.push({
            name: this._$translate.instant('Value'),
            identifier: vm.guid + 'sourcevalue',
            value: d => d.value,
            selector: d => d.guid + d.valueType,
            color: infoTextColor,
            symbol: 'circle',
            isLineVisible: false,
            isSymbolVisible: true,
            serieState: 0,
            crosshairFormat: d => formatter.format(d.value),
            isCrosshairHidden: false
        });
        result.push({
            name: this._$translate.instant('Target'),
            identifier: vm.guid + 'targetvalue',
            value: d => d.value,
            selector: d => d.guid + d.valueType,
            color: '#990',
            symbol: 'circle',
            isLineVisible: false,
            isSymbolVisible: true,
            serieState: 0,
            crosshairFormat: d => formatter.format(d.value),
            isCrosshairHidden: false
        });
        return result;
    }

    prepareData(data: { values: TagDateValue[], missing?: any }, beginDate: string, endDate: string): { stats: IStatistics, values: TagDateValue[] } {
        let missing = [];
        if (data.missing) {
            missing = this._seriesService.updateMissingSeries(data.missing as any);
        }
        let mapped = [];
        let stats: IStatistics = {} as any;
        if (data.values.length) {
            stats = this._statisticsService
                .getStatistics(data.values
                .filter(d => d !== null && d !== undefined)
                .map(d => {
                    return {
                        average: d.value
                    };
                }));
            mapped = this.mapStats(stats, beginDate, endDate);
        }
        const result = data.values
            .concat(missing)
            .concat(mapped);
        return {
            values: result,
            stats
        };
    }

    private mapStats(data: IStatistics, beginDate: string, endDate: string) {
        const data1 = {
            timeStamp: Date.parse(beginDate),
            average: data.average,
            stdDev: data.stdDev,
            gradient: data.intercept,
            name: 'stats',
            guid: ''
        };
        const data2 = {
            timeStamp: Date.parse(endDate),
            average: data.average,
            stdDev: data.stdDev,
            gradient: data.intercept + data.valuesCount * data.slope,
            name: 'stats',
            guid: ''
        };
        return [data1, data2];
    }
}